import { c as _c } from "react/compiler-runtime";
// Internal counterparty check a shop clears before it can trade.
//
// The founder distinguishes this sharply from `status` (active/blocked):
// «помимо того, что он должен себе бутик свой оккультурить, он внутреннюю нашу
// проверку должен ещё пройти» — documents, legal, accounting, catalog loaded,
// launched. She asked for it as ticks she can read at a glance, plus comments
// for the "what's missing" case («здесь ещё надо обязательно дать возможность
// комментария»).
//
// MOCK-ONLY: the gateway has no such record, so the block simply doesn't render
// when `verification` is absent — which is exactly what the live API returns.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Circle, MessageSquarePlus } from "lucide-react";
import type { StorefrontVerification } from "@/api";
import { Button } from "@osmix/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@osmix/ui/card";
import { Input } from "@osmix/ui/input";
import { formatDateish } from "@osmix/formatters";
import { cn } from "@/lib/utils";
export function VerificationChecklist(t0) {
  const $ = _c(60);
  const {
    verification
  } = t0;
  const {
    t
  } = useTranslation();
  const [comments, setComments] = useState(verification.comments);
  const [draft, setDraft] = useState("");
  let t1;
  if ($[0] !== verification.steps) {
    t1 = verification.steps.filter(_temp);
    $[0] = verification.steps;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const done = t1.length;
  const total = verification.steps.length;
  const complete = done === total;
  let t2;
  if ($[2] !== draft) {
    t2 = () => {
      const text = draft.trim();
      if (!text) {
        return;
      }
      setComments(current => [{
        id: `cmt_${Date.now()}`,
        at: new Date().toISOString(),
        author: "\u2014",
        text
      }, ...current]);
      setDraft("");
    };
    $[2] = draft;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const addComment = t2;
  let t3;
  if ($[4] !== t) {
    t3 = t("verification.title");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <CardTitle className="text-base">{t3}</CardTitle>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = complete ? "text-emerald-600 dark:text-emerald-500" : "text-amber-600 dark:text-amber-500";
  let t6;
  if ($[8] !== t5) {
    t6 = cn("text-sm font-semibold tabular-nums", t5);
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== done || $[11] !== t6 || $[12] !== total) {
    t7 = <span className={t6}>{done}/{total}</span>;
    $[10] = done;
    $[11] = t6;
    $[12] = total;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t4 || $[15] !== t7) {
    t8 = <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">{t4}{t7}</CardHeader>;
    $[14] = t4;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t || $[18] !== verification.steps) {
    let t10;
    if ($[20] !== t) {
      t10 = step_0 => <li key={step_0.key} className="flex items-start gap-3">{step_0.done ? <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-500"><Check className="size-3.5" /></span> : <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border border-dashed text-muted-foreground"><Circle className="size-2" /></span>}<div className="min-w-0 flex-1"><p className={cn("text-sm", !step_0.done && "text-muted-foreground")}>{t(`verification.steps.${step_0.key}`)}</p>{step_0.done && step_0.at && <p className="text-xs text-muted-foreground">{formatDateish(step_0.at)}{step_0.by ? ` · ${step_0.by}` : ""}</p>}</div></li>;
      $[20] = t;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    t9 = verification.steps.map(t10);
    $[17] = t;
    $[18] = verification.steps;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[22] !== t9) {
    t10 = <ul className="flex flex-col gap-2">{t9}</ul>;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t) {
    t11 = t("verification.comments");
    $[24] = t;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11) {
    t12 = <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t11}</p>;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== comments.length || $[29] !== t) {
    t13 = comments.length === 0 && <p className="text-sm text-muted-foreground">{t("verification.noComments")}</p>;
    $[28] = comments.length;
    $[29] = t;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== comments) {
    t14 = comments.map(_temp2);
    $[31] = comments;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t) {
    t15 = t("verification.addComment");
    $[33] = t;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = e => setDraft(e.target.value);
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== addComment) {
    t17 = e_0 => e_0.key === "Enter" && addComment();
    $[36] = addComment;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== draft || $[39] !== t15 || $[40] !== t17) {
    t18 = <Input value={draft} placeholder={t15} className="h-9" onChange={t16} onKeyDown={t17} />;
    $[38] = draft;
    $[39] = t15;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  const t19 = !draft.trim();
  let t20;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <MessageSquarePlus className="size-4" />;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== addComment || $[44] !== t19) {
    t21 = <Button size="sm" variant="outline" disabled={t19} onClick={addComment}>{t20}</Button>;
    $[43] = addComment;
    $[44] = t19;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t18 || $[47] !== t21) {
    t22 = <div className="flex items-center gap-2">{t18}{t21}</div>;
    $[46] = t18;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t12 || $[50] !== t13 || $[51] !== t14 || $[52] !== t22) {
    t23 = <div className="flex flex-col gap-2 border-t pt-3">{t12}{t13}{t14}{t22}</div>;
    $[49] = t12;
    $[50] = t13;
    $[51] = t14;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t10 || $[55] !== t23) {
    t24 = <CardContent className="flex flex-col gap-4">{t10}{t23}</CardContent>;
    $[54] = t10;
    $[55] = t23;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== t24 || $[58] !== t8) {
    t25 = <Card>{t8}{t24}</Card>;
    $[57] = t24;
    $[58] = t8;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  return t25;
}
function _temp2(comment) {
  return <div key={comment.id} className="rounded-lg border p-2"><p className="text-sm">{comment.text}</p><p className="text-xs text-muted-foreground">{comment.author} · {formatDateish(comment.at)}</p></div>;
}
function _temp(step) {
  return step.done;
}